import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../config/supabase';
import { useLanguage } from '../i18n/LanguageContext';
import { warpImageIntoQuad, Pt } from '../utils/perspectiveWarp.ts';
import { randomId } from '../utils/randomId';

// Customer-facing "see this door on your house" flow:
//   upload facade photo → AI detects the doorway → deterministic perspective
//   composite of the exact configured door → Gemini realism pass → result
//   shown with an AI disclaimer.
// Photos never persist server-side — the Edge Function processes in memory.

type Quad = [Pt, Pt, Pt, Pt];
type Phase = 'upload' | 'working' | 'result';

interface Props {
  onClose: () => void;
  // Front-view transparent PNG of the currently configured door (data URL).
  captureDoor: () => Promise<string | null>;
}

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const MAX_UPLOAD_DIM = 1600;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const COLORS = {
  gold: '#dfa926',
  goldBorder: 'rgba(223,169,38,0.45)',
  surface: 'rgba(20,20,22,0.97)',
  text: '#f9fafb',
  textMuted: '#9ca3af',
  border: 'rgba(255,255,255,0.1)',
  danger: '#f87171'
};

const quadFromDetection = (det: any): Quad | null => {
  const c = det?.corners;
  if (!c?.topLeft || !c?.topRight || !c?.bottomRight || !c?.bottomLeft) return null;
  const clamp = (p: any): Pt => ({
    x: Math.max(0, Math.min(1, Number(p.x) || 0)),
    y: Math.max(0, Math.min(1, Number(p.y) || 0))
  });
  return [clamp(c.topLeft), clamp(c.topRight), clamp(c.bottomRight), clamp(c.bottomLeft)];
};

const getSessionId = () => {
  try {
    let id = sessionStorage.getItem('aiViz.session');
    if (!id) {
      id = randomId();
      sessionStorage.setItem('aiViz.session', id);
    }
    return id;
  } catch {
    return randomId();
  }
};

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image_load_failed'));
    img.src = src;
  });

// Downscales the upload and re-encodes it as JPEG — keeps payloads small and
// strips any odd formats before they reach the API.
const preparePhoto = async (file: File) => {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('read_failed'));
    reader.readAsDataURL(file);
  });
  const img = await loadImage(dataUrl);
  const scale = Math.min(1, MAX_UPLOAD_DIM / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.round(img.naturalWidth * scale);
  const h = Math.round(img.naturalHeight * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas_failed');
  ctx.drawImage(img, 0, 0, w, h);
  return { dataUrl: canvas.toDataURL('image/jpeg', 0.88), img };
};

const AIDoorVisualizer: React.FC<Props> = ({ onClose, captureDoor }) => {
  const { t } = useLanguage();
  const [phase, setPhase] = useState<Phase>('upload');
  const [step, setStep] = useState<'detect' | 'render'>('detect');
  const [error, setError] = useState<string | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const tx = (key: string) => t(`configurator.aiViz.${key}`);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const mapError = (err: any): string => {
    const code = err?.message || (typeof err === 'object' ? JSON.stringify(err) : String(err));
    if (code.includes('rate_limit')) return tx('errors.rateLimit');
    if (code.includes('payload_too_large')) return tx('errors.tooLarge');
    if (code.includes('invalid_image')) return tx('errors.invalidFile');
    if (code.includes('no_door')) return tx('errors.noDoor');
    return tx('errors.failed');
  };

  const handleFile = async (file: File | undefined | null) => {
    if (!file) return;
    setError(null);
    if (!ACCEPTED_TYPES.includes(file.type)) { setError(tx('errors.invalidFile')); return; }
    if (file.size > MAX_FILE_BYTES) { setError(tx('errors.tooLarge')); return; }
    setStep('detect');
    setPhase('working');
    try {
      const { dataUrl, img } = await preparePhoto(file);
      const { data, error: fnError } = await supabase.functions.invoke('visualize-door', {
        body: {
          action: 'detect',
          sessionId: getSessionId(),
          imageBase64: dataUrl.split(',')[1],
          mimeType: 'image/jpeg'
        }
      });
      if (fnError) throw new Error(fnError.message || 'invoke_failed');
      if (data?.error) throw new Error(data.error);
      const quad = quadFromDetection(data?.detection);
      if (!quad) throw new Error('no_door');

      const doorDataUrl = await captureDoor();
      if (!doorDataUrl) throw new Error('capture_failed');
      const doorImg = await loadImage(doorDataUrl);

      // Deterministic composite: photo at native resolution + the door warped
      // into the detected quadrilateral.
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('canvas_failed');
      ctx.drawImage(img, 0, 0);
      const px: Quad = quad.map((p) => ({
        x: p.x * canvas.width,
        y: p.y * canvas.height
      })) as Quad;
      warpImageIntoQuad(ctx, doorImg, px);

      setStep('render');
      const composite = canvas.toDataURL('image/jpeg', 0.92);
      const { data: renderData, error: renderError } = await supabase.functions.invoke('visualize-door', {
        body: {
          action: 'render',
          sessionId: getSessionId(),
          compositeBase64: composite.split(',')[1],
          compositeMimeType: 'image/jpeg',
          doorBase64: doorDataUrl.split(',')[1],
          doorMimeType: 'image/png'
        }
      });
      if (renderError) throw new Error(renderError.message || 'invoke_failed');
      if (renderData?.error) throw new Error(renderData.error);
      const mime = renderData?.mimeType || 'image/png';
      setResultUrl(`data:${mime};base64,${renderData.imageBase64}`);
      setPhase('result');
    } catch (err) {
      setError(err instanceof Error && err.message === 'capture_failed' ? tx('errors.captureFailed') : mapError(err));
      setPhase('upload');
    }
  };

  const busy = phase === 'working';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={tx('title')}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(8,8,10,0.78)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem'
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div style={{
        width: '100%', maxWidth: '56rem', maxHeight: '92vh', overflowY: 'auto',
        background: COLORS.surface, border: `1px solid ${COLORS.border}`,
        borderRadius: '1rem', padding: '1.25rem 1.4rem',
        boxShadow: '0 24px 60px rgba(0,0,0,0.5)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', marginBottom: '0.9rem' }}>
          <h2 style={{ margin: 0, color: COLORS.text, fontSize: '1.1rem', fontWeight: 700 }}>{tx('title')}</h2>
          <button
            type="button" onClick={onClose} disabled={busy}
            style={{
              border: `1px solid ${COLORS.border}`, background: 'transparent', color: COLORS.textMuted,
              borderRadius: '0.5rem', padding: '0.35rem 0.7rem', cursor: busy ? 'default' : 'pointer', fontSize: '0.85rem'
            }}
          >
            {tx('close')}
          </button>
        </div>

        {error && (
          <div style={{
            marginBottom: '0.9rem', padding: '0.6rem 0.8rem', borderRadius: '0.6rem',
            border: '1px solid rgba(248,113,113,0.4)', background: 'rgba(248,113,113,0.1)',
            color: COLORS.danger, fontSize: '0.85rem'
          }}>
            {error}
          </div>
        )}

        {phase === 'upload' && (
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
            style={{
              border: `1.5px dashed ${COLORS.goldBorder}`, borderRadius: '0.9rem',
              padding: '2.4rem 1.5rem', textAlign: 'center', cursor: 'pointer',
              background: 'rgba(223,169,38,0.05)'
            }}
          >
            <div style={{ color: COLORS.gold, fontSize: '1rem', fontWeight: 600, marginBottom: '0.4rem' }}>{tx('uploadCta')}</div>
            <div style={{ color: COLORS.textMuted, fontSize: '0.82rem', lineHeight: 1.5 }}>{tx('uploadHint')}</div>
            <input
              ref={fileInputRef} type="file" accept={ACCEPTED_TYPES.join(',')}
              style={{ display: 'none' }}
              onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
            />
          </div>
        )}

        {phase === 'working' && (
          <div style={{ padding: '2.5rem 1rem', textAlign: 'center', color: COLORS.textMuted }}>
            <div style={{
              width: 36, height: 36, margin: '0 auto 0.9rem',
              border: '3px solid rgba(223,169,38,0.25)', borderTopColor: COLORS.gold,
              borderRadius: '50%', animation: 'aiVizSpin 0.8s linear infinite'
            }} />
            <style>{'@keyframes aiVizSpin { to { transform: rotate(360deg); } }'}</style>
            <div style={{ fontSize: '0.9rem' }}>{step === 'detect' ? tx('detecting') : tx('generating')}</div>
          </div>
        )}

        {phase === 'result' && resultUrl && (
          <>
            <img
              src={resultUrl} alt={tx('resultAlt')}
              style={{ display: 'block', maxWidth: '100%', maxHeight: '56vh', borderRadius: '0.6rem', marginBottom: '0.8rem' }}
            />
            <p style={{
              margin: '0 0 0.9rem', padding: '0.6rem 0.8rem', borderRadius: '0.6rem',
              border: `1px solid ${COLORS.goldBorder}`, background: 'rgba(223,169,38,0.07)',
              color: COLORS.textMuted, fontSize: '0.78rem', lineHeight: 1.55
            }}>
              {tx('disclaimer')}
            </p>
            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
              <a
                href={resultUrl} download="hestia-door-visualization.png"
                style={{
                  padding: '0.6rem 1.4rem', borderRadius: '0.6rem',
                  background: COLORS.gold, color: '#111', fontWeight: 700, fontSize: '0.9rem',
                  textDecoration: 'none'
                }}
              >
                {tx('download')}
              </a>
              <button
                type="button" onClick={() => { setPhase('upload'); setError(null); setResultUrl(null); }}
                style={{
                  padding: '0.6rem 1.1rem', borderRadius: '0.6rem',
                  border: `1px solid ${COLORS.border}`, background: 'transparent',
                  color: COLORS.textMuted, fontSize: '0.9rem', cursor: 'pointer'
                }}
              >
                {tx('newPhoto')}
              </button>
            </div>
          </>
        )}

        <p style={{ color: COLORS.textMuted, fontSize: '0.72rem', lineHeight: 1.5, margin: '0.9rem 0 0', opacity: 0.75 }}>
          {tx('privacyNote')}
        </p>
      </div>
    </div>
  );
};

export default AIDoorVisualizer;
